/**
 * Streaming SHA-256 — computes hash without loading the entire file into RAM.
 *
 * Uses file.stream() + incremental SHA-256 to process data in chunks.
 * Peak memory: ~64KB (one read chunk), NOT the full file size.
 *
 * Output format matches blocksuite/framework/global/src/utils/crypto.ts sha()
 * (base64url-encoded SHA-256).
 */
import { toBase64 } from 'lib0/buffer.js';

// SHA-256 round constants
const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotr(x: number, n: number): number {
  return ((x >>> n) | (x << (32 - n))) >>> 0;
}

class Sha256Hasher {
  private h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ]);
  private buffer = new Uint8Array(64);
  private bufferLen = 0;
  private totalLen = 0;

  update(data: Uint8Array): void {
    this.totalLen += data.length;
    let offset = 0;

    if (this.bufferLen > 0) {
      const needed = 64 - this.bufferLen;
      const toCopy = Math.min(needed, data.length);
      this.buffer.set(data.subarray(0, toCopy), this.bufferLen);
      this.bufferLen += toCopy;
      offset = toCopy;

      if (this.bufferLen === 64) {
        this.processBlock(this.buffer, 0);
        this.bufferLen = 0;
      }
    }

    while (offset + 64 <= data.length) {
      this.processBlock(data, offset);
      offset += 64;
    }

    if (offset < data.length) {
      this.buffer.set(data.subarray(offset), 0);
      this.bufferLen = data.length - offset;
    }
  }

  finalize(): Uint8Array {
    const totalBits = this.totalLen * 8;

    // Append 0x80 bit
    this.buffer[this.bufferLen++] = 0x80;

    // If not enough room for length (8 bytes), process current block and start new
    if (this.bufferLen > 56) {
      while (this.bufferLen < 64) this.buffer[this.bufferLen++] = 0;
      this.processBlock(this.buffer, 0);
      this.bufferLen = 0;
    }

    // Pad with zeros up to length field
    while (this.bufferLen < 56) this.buffer[this.bufferLen++] = 0;

    // Append total length as big-endian 64-bit integer
    const view = new DataView(this.buffer.buffer);
    // For files up to 256 TB, high 32 bits of bit-length
    view.setUint32(56, Math.floor(totalBits / 0x100000000));
    view.setUint32(60, totalBits >>> 0);
    this.processBlock(this.buffer, 0);

    // Produce final hash as Uint8Array(32)
    const result = new Uint8Array(32);
    const rv = new DataView(result.buffer);
    for (let i = 0; i < 8; i++) rv.setUint32(i * 4, this.h[i]);
    return result;
  }

  private processBlock(data: Uint8Array, offset: number): void {
    const w = new Uint32Array(64);

    // Read 16 big-endian 32-bit words from the block
    for (let i = 0; i < 16; i++) {
      const j = offset + i * 4;
      w[i] =
        ((data[j] << 24) |
          (data[j + 1] << 16) |
          (data[j + 2] << 8) |
          data[j + 3]) >>>
        0;
    }

    // Extend to 64 words
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let a = this.h[0],
      b = this.h[1],
      c = this.h[2],
      d = this.h[3],
      e = this.h[4],
      f = this.h[5],
      g = this.h[6],
      h = this.h[7];

    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    this.h[0] = (this.h[0] + a) >>> 0;
    this.h[1] = (this.h[1] + b) >>> 0;
    this.h[2] = (this.h[2] + c) >>> 0;
    this.h[3] = (this.h[3] + d) >>> 0;
    this.h[4] = (this.h[4] + e) >>> 0;
    this.h[5] = (this.h[5] + f) >>> 0;
    this.h[6] = (this.h[6] + g) >>> 0;
    this.h[7] = (this.h[7] + h) >>> 0;
  }
}

/**
 * Compute SHA-256 of a File using streaming reads.
 *
 * Output format: unpadded base64url, the key the server derives for a
 * content-addressed upload (Node `digest('base64url')`). A staged upload is
 * accepted only when both keys are equal, so this must not carry `=`.
 * Peak memory: ~chunk size (browser default ~64KB), NOT the full file.
 */
export async function streamingSha256(file: File): Promise<string> {
  const hasher = new Sha256Hasher();
  const reader = file.stream().getReader();

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    hasher.update(value);
  }

  const hash = hasher.finalize();
  return toBase64(hash)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/u, '');
}
